import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState, AppDispatch } from '.';
import {
  fetchSupportMessages,
  fetchSupportThread,
  markSupportRead,
  sendSupportMessage,
} from '../services/supportApi';
import type {
  SupportLocalMessage,
  SupportMessageDTO,
  SupportSubmitPayload,
  SupportThreadDTO,
} from '../types/support';
import { classifySupportFile } from '../utils/supportAttachments';
import {
  readSupportChatCache,
  type SupportChatCachePayload,
} from '../utils/supportChatCache';

/**
 * Стейт чата поддержки.
 * Хранит активный тред, список сообщений (включая локальные оптимистичные),
 * флаги загрузки/отправки и состояние UI виджета.
 */

interface SupportChatState {
  /** Активный тред пользователя. null = ещё ни разу не писал. */
  thread: SupportThreadDTO | null;
  /** Сообщения в хронологическом порядке (старые → новые). */
  messages: SupportLocalMessage[];
  /** UI: открыт ли чат-виджет. */
  isOpen: boolean;
  /** Загрузка треда (первичная). */
  loadingThread: boolean;
  /** Загрузка истории (первая страница). */
  loadingMessages: boolean;
  /** Подгрузка старых сообщений (бесконечный скролл). */
  loadingOlder: boolean;
  /** Отправка нового сообщения. */
  sending: boolean;
  /** Есть ли ещё более старые сообщения для подгрузки. */
  hasMore: boolean;
  /** Глобальная ошибка треда/истории. */
  error: string | null;
  /**
   * Один раз грузим тред при подключении к сокету / первом монтировании, чтобы
   * получить unread-бэйдж даже до открытия виджета.
   */
  threadInitialized: boolean;
  /** История восстановлена из localStorage (ещё может идти фоновая синхронизация). */
  cacheHydrated: boolean;
  /** id треда из кэша — для сверки с сервером. */
  cachedThreadId: string | null;
  /** Первая синхронизация с сервером после hydrate завершена. */
  messagesSynced: boolean;
  /** Фоновая подгрузка последних сообщений (без блокирующего спиннера). */
  syncingMessages: boolean;
  /** Полный bootstrap (кэш + тред + первая синхронизация) выполнен в этой сессии. */
  chatBootstrapped: boolean;
  /** AI-агент сейчас формирует ответ (WS-событие `support:typing`) — показать «печатает...». */
  isAgentTyping: boolean;
}

const initialState: SupportChatState = {
  thread: null,
  messages: [],
  isOpen: false,
  loadingThread: false,
  loadingMessages: false,
  loadingOlder: false,
  sending: false,
  hasMore: false,
  error: null,
  threadInitialized: false,
  cacheHydrated: false,
  cachedThreadId: null,
  messagesSynced: false,
  syncingMessages: false,
  chatBootstrapped: false,
  isAgentTyping: false,
};

const generateLocalId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `local-${crypto.randomUUID()}`;
  }
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const fromServerMessage = (m: SupportMessageDTO): SupportLocalMessage => ({
  id: m.id,
  threadId: m.threadId,
  direction: m.direction,
  text: m.text,
  createdAt: m.createdAt,
  telegramAdminUsername: m.telegramAdminUsername,
  attachments: m.attachments,
  localId: m.id,
  status: 'sent',
});

const sortByCreatedAtAsc = (a: SupportLocalMessage, b: SupportLocalMessage): number => {
  const aTs = new Date(a.createdAt).getTime();
  const bTs = new Date(b.createdAt).getTime();
  if (Number.isNaN(aTs) || Number.isNaN(bTs)) return 0;
  return aTs - bTs;
};

/** Слияние кэша и свежей страницы с сервера без мигания UI. */
const mergeSyncedMessages = (
  existing: SupportLocalMessage[],
  serverBatch: SupportMessageDTO[],
): SupportLocalMessage[] => {
  const pending = existing.filter((m) => m.status !== 'sent');
  const merged = new Map<string, SupportLocalMessage>();

  existing
    .filter((m) => m.status === 'sent' && m.id)
    .forEach((m) => merged.set(m.id!, m));

  serverBatch.forEach((m) => merged.set(m.id, fromServerMessage(m)));

  return [...merged.values(), ...pending].sort(sortByCreatedAtAsc);
};

const applyFreshMessagePage = (
  existing: SupportLocalMessage[],
  serverBatch: SupportMessageDTO[],
  hasMore: boolean,
): Pick<SupportChatState, 'messages' | 'hasMore'> => {
  const pending = existing.filter((m) => m.status !== 'sent');
  const next = serverBatch.map(fromServerMessage);
  return {
    messages: [...next, ...pending].sort(sortByCreatedAtAsc),
    hasMore,
  };
};

let initializeInFlight: Promise<void> | null = null;

const supportChatSlice = createSlice({
  name: 'supportChat',
  initialState,
  reducers: {
    openWidget: (state) => {
      state.isOpen = true;
    },
    closeWidget: (state) => {
      state.isOpen = false;
    },
    toggleWidget: (state) => {
      state.isOpen = !state.isOpen;
    },
    setThread: (state, action: PayloadAction<SupportThreadDTO | null>) => {
      state.thread = action.payload;
      state.threadInitialized = true;
    },
    setThreadInitialized: (state, action: PayloadAction<boolean>) => {
      state.threadInitialized = action.payload;
    },
    setUnread: (state, action: PayloadAction<number>) => {
      if (state.thread) {
        state.thread.unreadForUser = Math.max(0, action.payload);
      }
    },
    /** Внешний WS сигнал: тред поменял статус. */
    threadUpdatedFromSocket: (state, action: PayloadAction<SupportThreadDTO>) => {
      if (state.thread && state.thread.id !== action.payload.id) {
        state.thread = action.payload;
        state.messages = [];
        state.hasMore = false;
        state.cacheHydrated = false;
        state.cachedThreadId = null;
        state.messagesSynced = false;
      } else {
        state.thread = action.payload;
      }
      state.threadInitialized = true;
    },
    /** Восстановить историю из localStorage. */
    hydrateFromCache: (state, action: PayloadAction<SupportChatCachePayload>) => {
      const { thread, messages, hasMore, threadId } = action.payload;
      state.thread = thread;
      state.cachedThreadId = threadId;
      state.messages = messages.map(fromServerMessage);
      state.hasMore = hasMore;
      state.cacheHydrated = true;
      state.messagesSynced = false;
    },
    /** Сбросить устаревший кэш (другой тред / нет треда на сервере). */
    invalidateHydratedCache: (state) => {
      state.messages = [];
      state.hasMore = false;
      state.cacheHydrated = false;
      state.cachedThreadId = null;
      state.messagesSynced = false;
    },
    /** Замена истории при загрузке (первая страница). */
    setMessages: (
      state,
      action: PayloadAction<{ messages: SupportMessageDTO[]; hasMore: boolean }>,
    ) => {
      const next = action.payload.messages.map(fromServerMessage);
      // Сохраним оптимистичные локальные сообщения, которые ещё не сохранились на сервере
      const pending = state.messages.filter((m) => m.status !== 'sent');
      state.messages = [...next, ...pending].sort(sortByCreatedAtAsc);
      state.hasMore = action.payload.hasMore;
    },
    /** Prepend старых сообщений (пагинация). */
    prependMessages: (
      state,
      action: PayloadAction<{ messages: SupportMessageDTO[]; hasMore: boolean }>,
    ) => {
      const next = action.payload.messages.map(fromServerMessage);
      // дедуп по id
      const existingIds = new Set(state.messages.map((m) => m.id).filter(Boolean));
      const filtered = next.filter((m) => !existingIds.has(m.id));
      state.messages = [...filtered, ...state.messages];
      state.hasMore = action.payload.hasMore;
    },
    /** Добавить оптимистичное сообщение пользователя. */
    addOptimisticMessage: (state, action: PayloadAction<SupportLocalMessage>) => {
      state.messages.push(action.payload);
    },
    /** Подтвердить отправку оптимистичного сообщения. */
    confirmOptimisticMessage: (
      state,
      action: PayloadAction<{ localId: string; message: SupportMessageDTO }>,
    ) => {
      const { localId, message } = action.payload;
      const idx = state.messages.findIndex((m) => m.localId === localId);
      if (idx === -1) return;
      const previous = state.messages[idx];
      // освободим object URLs из превью
      previous.pendingAttachments?.forEach((p) => {
        try {
          URL.revokeObjectURL(p.previewUrl);
        } catch {}
      });
      state.messages[idx] = fromServerMessage(message);
    },
    /** Пометить оптимистичное сообщение как failed. */
    failOptimisticMessage: (
      state,
      action: PayloadAction<{ localId: string; error: string }>,
    ) => {
      const { localId, error } = action.payload;
      const m = state.messages.find((x) => x.localId === localId);
      if (m) {
        m.status = 'failed';
        m.errorMessage = error;
      }
    },
    /** Удалить локальное сообщение (повтор/удалить failed). */
    removeLocalMessage: (state, action: PayloadAction<string>) => {
      const localId = action.payload;
      const msg = state.messages.find((m) => m.localId === localId);
      msg?.pendingAttachments?.forEach((p) => {
        try {
          URL.revokeObjectURL(p.previewUrl);
        } catch {}
      });
      state.messages = state.messages.filter((m) => m.localId !== localId);
    },
    /** Входящее сообщение от админа через WS. */
    addAdminMessageFromSocket: (state, action: PayloadAction<SupportMessageDTO>) => {
      const message = action.payload;
      // дедуп
      if (state.messages.some((m) => m.id === message.id)) return;
      state.messages.push(fromServerMessage(message));
      state.messages.sort(sortByCreatedAtAsc);
      // Само появление сообщения — надёжный сигнал, что печать закончилась, даже если
      // отдельное support:typing{isTyping:false} по какой-то причине не дошло.
      state.isAgentTyping = false;
    },
    /** AI-агент начал/закончил формировать ответ (WS `support:typing`). */
    setAgentTyping: (state, action: PayloadAction<boolean>) => {
      state.isAgentTyping = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setChatBootstrapped: (state, action: PayloadAction<boolean>) => {
      state.chatBootstrapped = action.payload;
    },
    /** Полный сброс — при logout. */
    resetSupportChat: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadSupportThread.pending, (state) => {
        state.loadingThread = true;
        state.error = null;
      })
      .addCase(loadSupportThread.fulfilled, (state, action) => {
        state.loadingThread = false;
        state.thread = action.payload;
        state.threadInitialized = true;

        if (!action.payload) {
          if (state.cacheHydrated) {
            state.messages = [];
            state.hasMore = false;
            state.cacheHydrated = false;
            state.cachedThreadId = null;
            state.messagesSynced = false;
          }
          return;
        }

        if (state.cacheHydrated && state.cachedThreadId !== action.payload.id) {
          state.messages = [];
          state.hasMore = false;
          state.cacheHydrated = false;
          state.cachedThreadId = null;
          state.messagesSynced = false;
        }
      })
      .addCase(loadSupportThread.rejected, (state, action) => {
        state.loadingThread = false;
        state.error = (action.payload as string) || 'Не удалось загрузить тред';
        state.threadInitialized = true;
      })

      .addCase(loadSupportMessages.pending, (state, action) => {
        const background = Boolean(action.meta.arg && action.meta.arg.background);
        if (background) {
          state.syncingMessages = true;
        } else {
          state.loadingMessages = true;
        }
        state.error = null;
      })
      .addCase(loadSupportMessages.fulfilled, (state, action) => {
        state.loadingMessages = false;
        state.syncingMessages = false;
        state.messagesSynced = true;
        state.cacheHydrated = false;

        const background = Boolean(action.meta.arg && action.meta.arg.background);
        if (background) {
          state.messages = mergeSyncedMessages(state.messages, action.payload.messages);
        } else {
          Object.assign(
            state,
            applyFreshMessagePage(state.messages, action.payload.messages, action.payload.hasMore),
          );
          return;
        }
        state.hasMore = action.payload.hasMore;
      })
      .addCase(loadSupportMessages.rejected, (state, action) => {
        state.loadingMessages = false;
        state.syncingMessages = false;
        state.error = (action.payload as string) || 'Не удалось загрузить историю';
      })

      .addCase(loadOlderSupportMessages.pending, (state) => {
        state.loadingOlder = true;
      })
      .addCase(loadOlderSupportMessages.fulfilled, (state, action) => {
        state.loadingOlder = false;
        const next = action.payload.messages.map(fromServerMessage);
        const existingIds = new Set(state.messages.map((m) => m.id).filter(Boolean));
        const filtered = next.filter((m) => !existingIds.has(m.id));
        state.messages = [...filtered, ...state.messages];
        state.hasMore = action.payload.hasMore;
        state.messagesSynced = true;
      })
      .addCase(loadOlderSupportMessages.rejected, (state) => {
        state.loadingOlder = false;
      })

      .addCase(sendSupportMessageThunk.pending, (state) => {
        state.sending = true;
      })
      .addCase(sendSupportMessageThunk.fulfilled, (state) => {
        state.sending = false;
      })
      .addCase(sendSupportMessageThunk.rejected, (state) => {
        state.sending = false;
      });
  },
});

export const {
  openWidget,
  closeWidget,
  toggleWidget,
  setThread,
  setThreadInitialized,
  setUnread,
  threadUpdatedFromSocket,
  hydrateFromCache,
  invalidateHydratedCache,
  setMessages,
  prependMessages,
  addOptimisticMessage,
  confirmOptimisticMessage,
  failOptimisticMessage,
  removeLocalMessage,
  addAdminMessageFromSocket,
  setAgentTyping,
  setError,
  setChatBootstrapped,
  resetSupportChat,
} = supportChatSlice.actions;

// ===== Thunks =====

export const loadSupportThread = createAsyncThunk<
  SupportThreadDTO | null,
  void,
  { state: RootState; rejectValue: string }
>('supportChat/loadThread', async (_, { rejectWithValue }) => {
  try {
    const thread = await fetchSupportThread();
    return thread;
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Не удалось загрузить тред');
  }
});

export const loadSupportMessages = createAsyncThunk<
  { messages: SupportMessageDTO[]; hasMore: boolean },
  { limit?: number; background?: boolean } | void,
  { state: RootState; rejectValue: string }
>('supportChat/loadMessages', async (args, { rejectWithValue }) => {
  try {
    const limit = args && 'limit' in args ? args.limit ?? 50 : 50;
    return await fetchSupportMessages({ limit });
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Не удалось загрузить историю');
  }
});

/**
 * Первичная инициализация: hydrate из localStorage → тред с сервера → синхронизация истории.
 * Безопасно вызывать несколько раз — повторные вызовы ждут текущую инициализацию.
 */
export const initializeSupportChat = createAsyncThunk<
  void,
  string | number,
  { state: RootState; dispatch: AppDispatch }
>('supportChat/initialize', async (userId, { dispatch, getState }) => {
  const run = async () => {
    if (getState().supportChat.chatBootstrapped) return;

    const initial = getState().supportChat;

    if (initial.messages.length === 0 && !initial.cacheHydrated) {
      const cached = readSupportChatCache(userId);
      if (cached) {
        dispatch(hydrateFromCache(cached));
      }
    }

    await dispatch(loadSupportThread());

    const afterThread = getState().supportChat;
    const thread = afterThread.thread;

    if (!thread) {
      if (afterThread.cacheHydrated) {
        dispatch(invalidateHydratedCache());
      }
      dispatch(setChatBootstrapped(true));
      return;
    }

    if (afterThread.cacheHydrated && afterThread.cachedThreadId !== thread.id) {
      dispatch(invalidateHydratedCache());
      await dispatch(loadSupportMessages({ limit: 50 }));
      dispatch(setChatBootstrapped(true));
      return;
    }

    const hasCachedMessages =
      afterThread.cacheHydrated && afterThread.messages.length > 0;

    if (hasCachedMessages || afterThread.messages.length > 0) {
      await dispatch(loadSupportMessages({ limit: 50, background: true }));
    } else {
      await dispatch(loadSupportMessages({ limit: 50 }));
    }

    dispatch(setChatBootstrapped(true));
  };

  if (initializeInFlight) {
    await initializeInFlight;
    return;
  }

  initializeInFlight = run().finally(() => {
    initializeInFlight = null;
  });

  await initializeInFlight;
});

export const loadOlderSupportMessages = createAsyncThunk<
  { messages: SupportMessageDTO[]; hasMore: boolean },
  void,
  { state: RootState; rejectValue: string }
>('supportChat/loadOlder', async (_, { getState, rejectWithValue }) => {
  try {
    const { messages } = getState().supportChat;
    const oldest = messages.find((m) => m.id) ?? null;
    if (!oldest || !oldest.id) {
      return { messages: [], hasMore: false };
    }
    return await fetchSupportMessages({ limit: 50, before: oldest.id });
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Не удалось загрузить старые сообщения');
  }
});

/**
 * Отправить сообщение от пользователя — с оптимистичным UI.
 */
export const sendSupportMessageThunk = createAsyncThunk<
  void,
  SupportSubmitPayload,
  { state: RootState; rejectValue: string }
>('supportChat/sendMessage', async (payload, { dispatch, rejectWithValue }) => {
  const { text, attachments, voice } = payload;
  const localId = generateLocalId();
  const now = new Date().toISOString();

  const pendingAttachments = [
    ...(voice
      ? [
          {
            localId: generateLocalId(),
            name: voice.file.name,
            kind: 'voice' as const,
            mimeType: voice.file.type,
            size: voice.file.size,
            previewUrl: URL.createObjectURL(voice.file),
            durationSec: voice.durationSec,
          },
        ]
      : []),
    ...attachments.map(({ file: f, durationSec }) => ({
      localId: generateLocalId(),
      name: f.name,
      kind: classifySupportFile(f) ?? ('file' as const),
      mimeType: f.type,
      size: f.size,
      previewUrl: URL.createObjectURL(f),
      durationSec,
    })),
  ];

  const optimistic: SupportLocalMessage = {
    id: null,
    threadId: null,
    direction: 'user_to_admin',
    text: text || null,
    createdAt: now,
    telegramAdminUsername: null,
    attachments: [],
    localId,
    status: 'sending',
    pendingAttachments: pendingAttachments.length > 0 ? pendingAttachments : undefined,
  };
  dispatch(addOptimisticMessage(optimistic));

  try {
    const message = await sendSupportMessage(payload);
    dispatch(confirmOptimisticMessage({ localId, message }));
    // Если тред ещё не был загружен (первое сообщение) — синхронизируем его
    dispatch(loadSupportThread());
  } catch (err: any) {
    const errMsg = err?.message || 'Не удалось отправить сообщение';
    dispatch(failOptimisticMessage({ localId, error: errMsg }));
    return rejectWithValue(errMsg);
  }
});

/** Сбросить unread и отправить /support/read. */
export const markSupportThreadRead = createAsyncThunk<
  void,
  void,
  { state: RootState }
>('supportChat/markRead', async (_, { dispatch, getState }) => {
  const thread = getState().supportChat.thread;
  if (!thread || thread.unreadForUser === 0) return;
  dispatch(setUnread(0));
  try {
    await markSupportRead();
  } catch {
    // ошибка не критична — UI уже сброшен; счётчик восстановится при следующем WS
  }
});

// ===== Selectors =====

export const selectSupportChat = (state: RootState) => state.supportChat;
export const selectSupportThread = (state: RootState) => state.supportChat.thread;
export const selectSupportMessages = (state: RootState) => state.supportChat.messages;
export const selectSupportIsOpen = (state: RootState) => state.supportChat.isOpen;
export const selectSupportUnread = (state: RootState) =>
  state.supportChat.thread?.unreadForUser ?? 0;
export const selectSupportSending = (state: RootState) => state.supportChat.sending;
export const selectSupportHasMore = (state: RootState) => state.supportChat.hasMore;
export const selectSupportLoadingThread = (state: RootState) =>
  state.supportChat.loadingThread;
export const selectSupportLoadingMessages = (state: RootState) =>
  state.supportChat.loadingMessages;
export const selectSupportLoadingOlder = (state: RootState) =>
  state.supportChat.loadingOlder;
export const selectSupportThreadInitialized = (state: RootState) =>
  state.supportChat.threadInitialized;
export const selectSupportCacheHydrated = (state: RootState) =>
  state.supportChat.cacheHydrated;
export const selectSupportMessagesSynced = (state: RootState) =>
  state.supportChat.messagesSynced;
export const selectSupportSyncingMessages = (state: RootState) =>
  state.supportChat.syncingMessages;
export const selectSupportError = (state: RootState) => state.supportChat.error;
export const selectSupportAgentTyping = (state: RootState) => state.supportChat.isAgentTyping;

export default supportChatSlice.reducer;
